import { Parser, Ty } from '@cotera/era';
import {
  containsTemplateRef,
  jsonPointerGet,
  parseToolCallParamBindingRef,
} from '@cotera/utilities';
import type { WorkflowNodeOutputShapeContext } from '../workflow-node-output-shape';

/**
 * One output field of a mapper node: the name it publishes, and where the value
 * comes from.
 *
 * `template` fields are strings with `{{ref}}` tokens spliced into them and so
 * are always typed `string`; a plain `ref` field carries the referenced value
 * whole, with its type intact.
 */
export type MapperField = {
  readonly key: string;
  readonly ref: string;
  readonly template: boolean;
};

/**
 * What a mapper publishes:
 *  - `record` (the default): one object, a property per field.
 *  - `row`: `{ [rowKey]: (string | null)[] }` — every field's value flattened
 *    to text (an empty value stays `null`), in field order. The shape a spreadsheet "append row" step takes, so a
 *    record can be written out as one row without an agent in between. Field
 *    keys don't appear in this output; only their order does.
 */
export type MapperOutputMode = 'record' | 'row';

/** The property a `row` mapper publishes its cells under, unless renamed. */
export const MAPPER_DEFAULT_ROW_KEY = 'row';

function asDataRecord(data: unknown): Record<string, unknown> | null {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  return data as Record<string, unknown>;
}

export function readMapperOutputMode(data: unknown): MapperOutputMode {
  return asDataRecord(data)?.['output'] === 'row' ? 'row' : 'record';
}

/** The property a `row` mapper publishes its list under (`rowKey`). */
export function readMapperRowKey(data: unknown): string {
  const raw = asDataRecord(data)?.['rowKey'];
  return typeof raw === 'string' && raw.trim() !== ''
    ? raw.trim()
    : MAPPER_DEFAULT_ROW_KEY;
}

/**
 * One cell of a `row` mapper's output. Text passes through as-is; numbers and
 * booleans print the way they read; objects and lists become compact JSON.
 *
 * A missing or null value stays `null` rather than `""`: a table-append tool
 * writes `null` as SQL NULL, while `""` is rejected by a NUMBER or TIMESTAMP
 * column. Where the tool wants text cells (a spreadsheet row), the tool-call
 * runner fills `null` in as a blank.
 */
export function mapperRowCell(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return JSON.stringify(value) ?? '';
}

/**
 * `fieldOrder` pins the order fields were authored in. Node data is stored as
 * JSONB, which does not keep object key order, so `fieldBindings` alone comes
 * back reshuffled — harmless for a record, but a row's columns ARE its order.
 * Keys `fieldOrder` doesn't mention (older nodes, hand-written configs) follow
 * in whatever order the bindings object holds them.
 */
function orderFields(
  fields: readonly MapperField[],
  rawOrder: unknown
): readonly MapperField[] {
  if (!Array.isArray(rawOrder)) {
    return fields;
  }
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const ordered: MapperField[] = [];
  for (const entry of rawOrder) {
    if (typeof entry !== 'string') {
      continue;
    }
    const field = byKey.get(entry.trim());
    if (field !== undefined) {
      ordered.push(field);
      byKey.delete(field.key);
    }
  }
  return [...ordered, ...byKey.values()];
}

/**
 * The mapper's declared fields, in the order they were authored, skipping
 * entries that name nothing. Shared by the runner, the editor and the output
 * shape derivation so all three read one node the same way.
 */
export function readMapperFields(data: unknown): readonly MapperField[] {
  const record = asDataRecord(data);
  if (record === null) {
    return [];
  }
  const bindings = record['fieldBindings'];
  if (
    bindings === null ||
    bindings === undefined ||
    typeof bindings !== 'object' ||
    Array.isArray(bindings)
  ) {
    return [];
  }
  const fields: MapperField[] = [];
  for (const [key, ref] of Object.entries(
    bindings as Record<string, unknown>
  )) {
    if (typeof ref !== 'string' || ref.trim() === '' || key.trim() === '') {
      continue;
    }
    fields.push({
      key: key.trim(),
      ref: ref.trim(),
      template: containsTemplateRef(ref),
    });
  }
  return orderFields(fields, record['fieldOrder']);
}

/**
 * The ERA type of one mapped field, read off the sample its source publishes.
 *
 * Anything that can't be typed confidently — a source whose shape isn't
 * resolvable here, a pointer that reaches nothing, an empty array — degrades to
 * `super` for that field alone. Field-by-field rather than all-or-nothing on
 * purpose: one unresolvable ref should not cost the downstream picker every
 * other field's name.
 */
function mapperFieldTy(
  field: MapperField,
  nodeId: string,
  context: WorkflowNodeOutputShapeContext
): Ty.Shorthand {
  if (field.template) {
    return 'string';
  }
  let parsed: ReturnType<typeof parseToolCallParamBindingRef>;
  try {
    parsed = parseToolCallParamBindingRef(field.ref);
  } catch {
    return Ty.ty('super');
  }
  // A bare pointer (no source segment) reads the direct predecessor, which is
  // also what the runner falls back to.
  const sourceSample =
    parsed.sourceName === undefined
      ? context.resolvePredecessorOutputSample?.(nodeId)
      : context.resolveUpstreamSample?.(parsed.sourceName);
  if (sourceSample === null || sourceSample === undefined) {
    return Ty.ty('super');
  }
  let value: unknown;
  try {
    value = jsonPointerGet(sourceSample, parsed.pointer);
  } catch {
    return Ty.ty('super');
  }
  if (value === undefined) {
    return Ty.ty('super');
  }
  // `maxVariantNumber: 0` keeps a sampled string typed `string` rather than
  // collapsing into an enum of the one placeholder value the sample happened to
  // carry.
  const inferred = Parser.inferType([value as Ty.Scalar], {
    maxVariantNumber: 0,
  });
  return inferred ?? Ty.ty('super');
}

/**
 * The ERAQL shape a mapper publishes: a struct with one field per mapping, or
 * for a `row` mapper, `{ [rowKey]: string[] }`.
 *
 * This is the whole point of the node — a step several levels down binds to the
 * mapper's flat, named output instead of reaching back through the graph, and
 * the `/`-picker on that step can only offer those names if the shape carries
 * them.
 *
 * Returns null when nothing is mapped; the runner passes its input through in
 * that case, and the caller mirrors the predecessor's shape instead.
 */
export function deriveMapperOutputShape(params: {
  readonly nodeId: string;
  readonly data: unknown;
  readonly context: WorkflowNodeOutputShapeContext;
}): string | null {
  const fields = readMapperFields(params.data);
  if (fields.length === 0) {
    return null;
  }
  if (readMapperOutputMode(params.data) === 'row') {
    return Ty.displayTy(
      Ty.structOf({
        [readMapperRowKey(params.data)]: Ty.arrayOf({
          ty: 'string',
          nullable: true,
        }),
      })
    );
  }
  const tys: Record<string, Ty.Shorthand> = {};
  for (const field of fields) {
    tys[field.key] = mapperFieldTy(field, params.nodeId, params.context);
  }
  return Ty.displayTy(Ty.structOf(tys));
}
